import React, { useState } from 'react';
import { Eye, Download, FileText } from 'lucide-react';
import ImageCarouselModal from './ImageCarouselModal';

function DeliveriesTable({ limaDeliveries = [], provinciaDeliveries = [], onExport, showUserColumn }) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('lima'); // 'lima', 'provincia', 'dropship'

  function handleViewImages(images) {
    setSelectedImages(images);
    setShowModal(true);
  }

  function truncateName(name) {
    if (!name) return '-';
    return name.length > 10 ? name.substring(0, 10) + '...' : name;
  }

  function getStatusBadge(status) {
    const statusConfig = {
      entregado: {
        label: 'Entregado',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        borderColor: 'border-green-300'
      },
      pending: {
        label: 'Pendiente',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-300'
      },
      cancelado: {
        label: 'Cancelado',
        bgColor: 'bg-slate-100',
        textColor: 'text-slate-700',
        borderColor: 'border-slate-300'
      },
      rechazado: {
        label: 'Rechazado',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        borderColor: 'border-red-300'
      },
      pospuesto: {
        label: 'Pospuesto',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-300'
      },
      encamino: {
        label: 'En Camino',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-300'
      }
    };

    const normalizedStatus = status ? status.toLowerCase() : 'pending';
    const config = statusConfig[normalizedStatus] || statusConfig.pending;

    return (
      <span className={`
        inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold font-montserrat
        ${config.bgColor} ${config.textColor} border ${config.borderColor}
      `}>
        {config.label}
      </span>
    );
  }

  // Filter deliveries based on active tab
  const allDeliveries = [...limaDeliveries, ...provinciaDeliveries];
  const dropshipDeliveries = allDeliveries.filter(d => d.is_dropship);
  
  let currentDeliveries = [];
  if (activeTab === 'lima') {
    currentDeliveries = limaDeliveries;
  } else if (activeTab === 'provincia') {
    currentDeliveries = provinciaDeliveries;
  } else if (activeTab === 'dropship') {
    currentDeliveries = dropshipDeliveries;
  }

  if (allDeliveries.length === 0) {
    return (
      <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center">
        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 font-montserrat">
          No hay entregas para mostrar
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
      {/* Tabs and Export Button */}
      <div className="p-4 bg-slate-50 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('lima')}
              className={`px-4 py-2 rounded-lg font-semibold font-montserrat transition-all ${
                activeTab === 'lima'
                  ? 'bg-brand-primary text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              Lima ({limaDeliveries.length})
            </button>
            <button
              onClick={() => setActiveTab('provincia')}
              className={`px-4 py-2 rounded-lg font-semibold font-montserrat transition-all ${
                activeTab === 'provincia'
                  ? 'bg-brand-gold text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              Provincia ({provinciaDeliveries.length})
            </button>
            <button
              onClick={() => setActiveTab('dropship')}
              className={`px-4 py-2 rounded-lg font-semibold font-montserrat transition-all ${
                activeTab === 'dropship'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              Dropship ({dropshipDeliveries.length})
            </button>
          </div>

          {/* Export Button */}
          <button
            onClick={onExport}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-all font-montserrat font-semibold"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
  <table className="w-full">
    <thead className="bg-slate-100">
      <tr>
        <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">ID</th>
        <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Tipo</th>
        {showUserColumn && (
          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Usuario</th>
        )}
        <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Estatus</th>
        <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Destinatario</th>
        <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Ubicación</th>
        <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 font-montserrat">Notas</th>
        
        {/* Only show financial columns if NOT on dropship tab */}
        {activeTab !== 'dropship' && (
          <>
            {/* Lima-specific columns */}
            {activeTab === 'lima' && (
              <>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Cargo</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Cobrado</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">FastMax</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Vuelto</th>
              </>
            )}
            
            {/* Provincia-specific columns */}
            {activeTab === 'provincia' && (
              <>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Cobrado</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Adelanto</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Transporte</th>
              </>
            )}
          </>
        )}
        
        {/* Dropship cost column - show when on dropship tab OR when there are dropship deliveries in current view */}
        {(activeTab === 'dropship' || currentDeliveries.some(d => d.is_dropship)) && (
          <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 font-montserrat">Dropship</th>
        )}
        
        <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 font-montserrat">Imágenes</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-200">
      {currentDeliveries.map((delivery) => (
        <tr key={`${delivery.type}-${delivery.id}`} className="hover:bg-slate-50 transition-colors">
          <td className="px-4 py-3 text-sm text-slate-800 font-montserrat font-semibold">
            #{delivery.id}
          </td>
          <td className="px-4 py-3">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold font-montserrat ${
              delivery.type === 'Lima' 
                ? 'bg-brand-primary/10 text-brand-primary' 
                : 'bg-brand-gold/10 text-brand-gold'
            }`}>
              {delivery.type}
            </span>
            {delivery.is_dropship && (
              <span className="ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-bold font-montserrat bg-purple-100 text-purple-700">
                DS
              </span>
            )}
          </td>
          {showUserColumn && (
            <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
              {delivery.user_name || '-'}
            </td>
          )}
          <td className="px-4 py-3">
            {getStatusBadge(delivery.status)}
          </td>
          <td className="px-4 py-3 text-sm text-slate-800 font-montserrat" title={delivery.recipient_name}>
            {truncateName(delivery.recipient_name)}
          </td>
          <td className="px-4 py-3 text-sm text-slate-600 font-montserrat">
            {delivery.type === 'Provincia' 
              ? `${delivery.province} / ${delivery.district}`
              : delivery.district || '-'
            }
          </td>
          <td className="px-4 py-3 text-sm text-slate-600 font-montserrat max-w-xs truncate" title={delivery.notes_driver || delivery.notes}>
            {delivery.type === 'Lima' 
              ? (delivery.notes_driver ? (delivery.notes_driver.length > 18 ? delivery.notes_driver.substring(0, 18) + '...' : delivery.notes_driver) : '-')
              : (delivery.notes ? (delivery.notes.length > 18 ? delivery.notes.substring(0, 18) + '...' : delivery.notes) : '-')
            }
          </td>
          
          {/* Only show financial columns if NOT on dropship tab */}
          {activeTab !== 'dropship' && (
            <>
              {/* Lima columns - only show when on Lima tab AND delivery is Lima type */}
              {activeTab === 'lima' && delivery.type === 'Lima' && (
                <>
                  <td className="px-4 py-3 text-sm text-right text-slate-800 font-montserrat font-semibold">
                    S/. {(delivery.charge || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-brand-primary font-montserrat font-bold">
                    S/. {(delivery.charged || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-brand-gold font-montserrat font-bold">
                    S/. {(delivery.fastmax_transfer_amount || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-600 font-montserrat font-semibold">
                    S/. {(delivery.fastmax_exchange_amount || 0).toFixed(2)}
                  </td>
                </>
              )}
              
              {/* Provincia columns - only show when on Provincia tab AND delivery is Provincia type */}
              {activeTab === 'provincia' && delivery.type === 'Provincia' && (
                <>
                  <td className="px-4 py-3 text-sm text-right text-brand-gold font-montserrat font-bold">
                    S/. {(delivery.charged || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-brand-gold font-montserrat font-bold">
                    S/. {(delivery.deposit_advance || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-red-600 font-montserrat font-semibold">
                    S/. {(delivery.transport_price || 0).toFixed(2)}
                  </td>
                </>
              )}
            </>
          )}
          
          {/* Dropship cost column */}
          {(activeTab === 'dropship' || currentDeliveries.some(d => d.is_dropship)) && (
            <td className="px-4 py-3 text-sm text-right font-montserrat font-bold">
              {delivery.is_dropship ? (
                <span className="text-purple-600">
                  S/. {(delivery.dropship_cost || delivery.dropship_earnings || 0).toFixed(2)}
                </span>
              ) : (
                <span className="text-slate-400">-</span>
              )}
            </td>
          )}
          
          <td className="px-4 py-3 text-center">
            {delivery.image_urls && delivery.image_urls.length > 0 ? (
              <button
                onClick={() => handleViewImages(delivery.image_urls)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-primary/10 text-brand-primary rounded-lg hover:bg-brand-primary/20 transition-all font-montserrat font-semibold text-xs"
              >
                <Eye className="w-4 h-4" />
                {delivery.image_urls.length}
              </button>
            ) : (
              <span className="text-slate-400 text-xs font-montserrat">Sin imágenes</span>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

      {/* Image Modal */}
      {showModal && (
        <ImageCarouselModal
          images={selectedImages}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default DeliveriesTable;